import numeral from 'numeral';
import { CURRENCY_SYMBOLS } from '../constants/shared.constants';
import { Currency } from '../enums/shared.enums';
import { BalanceAmount } from '../@types/shared.types';

// ----------------------------------------------------------------------

export function fCurrency(number: string | number, symbol = '$') {
    return numeral(number).format(Number.isInteger(number) ? `${symbol}0,0` : `${symbol}0,0.00`);
}

export function fPercent(number: number) {
    return numeral(number / 100).format('0.0%');
}

export function fNumber(number: string | number) {
    return numeral(number).format();
}

export function fShortenNumber(number: string | number) {
    return numeral(number).format('0.00a').replace('.00', '');
}

export function fData(number: string | number) {
    return numeral(number).format('0.0 b');
}

export function fPrice(cents: any, currency: Currency = Currency.USD) {
    const symbol = CURRENCY_SYMBOLS[currency];

    let val = (Math.abs(cents) / 100).toFixed(2);
    // Now add commas
    val = val.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const sign = cents < 0 ? '-' : '';
    return sign + symbol + val;
}

/**
 * Cents as a plain decimal amount ("30.00"), with no symbol or separators so a
 * spreadsheet reads it as a number. Missing amounts become an empty string.
 */
export function centsToDollars(cents: number | null | undefined): string {
    return cents == null ? '' : (cents / 100).toFixed(2);
}

export function fBalance(balance: [BalanceAmount] | null) {
    if (!balance) {
        return '';
    }
    return fPrice(balance[0].amount, balance[0].currency);
}

export function fPhone(phone: string | null | undefined) {
    if (!phone) {
        return '';
    }
    if (phone.startsWith('+1')) {
        // +12344568900 => +1 (234) 456-8900
        const countryCode = phone.slice(0, 2);
        const areaCode = phone.slice(2, 5);
        const first3 = phone.slice(5, 8);
        const last4 = phone.slice(8);

        return `${countryCode} (${areaCode}) ${first3}-${last4}`;
    } else if (phone.startsWith('+44')) {
        // +447700159458 => +44 7700 159458
        const countryCode = phone.slice(0, 3);
        const first4 = phone.slice(3, 7);
        const lastX = phone.slice(7);
        return `${countryCode} ${first4} ${lastX}`;
    } else if (phone.startsWith('+61')) {
        // +61412345678 => +61 412 345 678
        const countryCode = phone.slice(0, 3);
        const first3 = phone.slice(3, 6);
        const second3 = phone.slice(6, 9);
        const third3 = phone.slice(9);
        return `${countryCode} ${first3} ${second3} ${third3}`;
    } else if (phone.startsWith('+63')) {
        // +639123456789 => +63 912 345 6789
        const countryCode = phone.slice(0, 3);
        const first3 = phone.slice(3, 6);
        const middle3 = phone.slice(6, 9);
        const last4 = phone.slice(9);
        return `${countryCode} ${first3} ${middle3} ${last4}`;
    }
    return phone;
}

export function fTime(
    amount: number,
    unit: 'days' | 'hours' | 'minutes' | 'seconds'
): {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
} {
    let duration = amount;

    if (unit === 'minutes') {
        duration = amount * 60;
    }
    if (unit === 'hours') {
        duration = amount * 60 * 60;
    }
    if (unit === 'days') {
        duration = amount * 60 * 60 * 24;
    }
    const days = Math.floor(duration / (60 * 60 * 24));
    const hours = Math.floor((duration % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((duration % (60 * 60)) / 60);
    const seconds = Math.floor(duration % 60);
    return {
        days,
        hours,
        minutes,
        seconds,
    };
}

// For example, (+12345678900, +1) => 2345678900, (12345678900, +1) => 2345678900, (+447700159458, +44)=> 7700159458, (447700159458, +44) => 7700159458
// The countryCode parameter should begin with a '+'
export function cleanPhoneNumber(phoneNumber: string, countryCode: string): string {
    if (!phoneNumber || !countryCode) {
        return phoneNumber;
    }

    // Remove non-numeric characters
    phoneNumber = phoneNumber.replace(/\D/g, '');
    countryCode = countryCode.replace(/\D/g, '');

    if (phoneNumber.startsWith(countryCode)) {
        return phoneNumber.slice(countryCode.length);
    }
    return phoneNumber;
}

export function applyPhoneRules(phoneNumber: string, countryCode: string): string {
    // Clean the phone number
    phoneNumber = cleanPhoneNumber(phoneNumber, countryCode);
    switch (countryCode) {
        case '+1':
            return applyUSPhoneRules(phoneNumber);
        case '+44':
            return applyGBPhoneRules(phoneNumber);
        case '+61':
            return applyAUPhoneRules(phoneNumber);
        case '+63':
            return applyPHPhoneRules(phoneNumber);
        default:
            return phoneNumber;
    }
}

function applyUSPhoneRules(phoneNumber: string) {
    // Return the first 10 digits
    return phoneNumber.slice(0, 10);
}

function applyGBPhoneRules(phoneNumber: string) {
    // Return the first 11 digits
    return phoneNumber.slice(0, 11);
}

function applyAUPhoneRules(phoneNumber: string) {
    // Return the first 9 digits (Australian mobile and landline)
    return phoneNumber.slice(0, 9);
}

function applyPHPhoneRules(phoneNumber: string) {
    // Return the first 10 digits (Philippine mobile numbers)
    return phoneNumber.slice(0, 10);
}

export function applyPhoneFormat(phoneNumber: string, countryCode: string): string {
    // Clean the phone number
    phoneNumber = cleanPhoneNumber(phoneNumber, countryCode);
    switch (countryCode) {
        case '+1':
            return applyUSPhoneFormat(phoneNumber);
        case '+44':
            return applyGBPhoneFormat(phoneNumber);
        case '+61':
            return applyAUPhoneFormat(phoneNumber);
        case '+63':
            return applyPHPhoneFormat(phoneNumber);
        default:
            return phoneNumber;
    }
}

function applyUSPhoneFormat(phoneNumber: string) {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(1|)?(\d{0,3})?(\d{0,3})?(\d{0,4})?$/);
    let result = '';

    if (match) {
        const intlCode = match[1] ? '+1 ' : '';
        if (match[2]) {
            result += ['(', match[2]].join('');
        }
        if (match[3]) {
            result += [') ', match[3]].join('');
        }
        if (match[4]) {
            result += ['-', match[4]].join('');
        }
        return intlCode + result;
    }
    return phoneNumber;
}

function applyGBPhoneFormat(phoneNumber: string) {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(44|)?(\d{0,3})?(\d{0,4})?(\d{0,4})?$/);
    let result = '';

    if (match) {
        const intlCode = match[1] ? '+44 ' : '';
        if (match[2]) {
            result += [match[2], ' '].join('');
        }
        if (match[3]) {
            result += [match[3], ' '].join('');
        }
        if (match[4]) {
            result += [match[4]].join('');
        }
        return intlCode + result;
    }
    return phoneNumber;
}

function applyAUPhoneFormat(phoneNumber: string) {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(61|)?(\d{0,3})?(\d{0,3})?(\d{0,3})?$/);
    let result = '';

    if (match) {
        const intlCode = match[1] ? '+61 ' : '';
        if (match[2]) {
            result += [match[2], ' '].join('');
        }
        if (match[3]) {
            result += [match[3], ' '].join('');
        }
        if (match[4]) {
            result += [match[4]].join('');
        }
        return intlCode + result;
    }
    return phoneNumber;
}

function applyPHPhoneFormat(phoneNumber: string) {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    const match = cleaned.match(/^(63|)?(\d{0,3})?(\d{0,3})?(\d{0,4})?$/);
    let result = '';

    if (match) {
        const intlCode = match[1] ? '+63 ' : '';
        if (match[2]) {
            result += [match[2], ' '].join('');
        }
        if (match[3]) {
            result += [match[3], ' '].join('');
        }
        if (match[4]) {
            result += [match[4]].join('');
        }
        return intlCode + result;
    }
    return phoneNumber;
}
